import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Subscription from '../components/Subscription';
import './BlogPostPage.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch the main post
        const { data: postData, error: postError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .single();

        if (postError) throw postError;
        if (!postData) throw new Error('Post not found');

        // If the post has chunks, fetch and combine them
        if (postData.has_chunks) {
          const { data: chunks, error: chunksError } = await supabase
            .from('blog_content_chunks')
            .select('*')
            .eq('post_id', postData.id)
            .order('chunk_index', { ascending: true });

          if (chunksError) throw chunksError;

          // Combine chunks into single content
          postData.content = chunks.map(chunk => chunk.content).join('');
        }

        setPost(postData);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Function to format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="blog-post-page">
        <Header />
        <main className="post-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-message">Loading post...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-page">
        <Header />
        <main className="post-content">
          <div className="error-container">
            <h1>Error</h1>
            <p>{error || 'Post not found'}</p>
            <button onClick={() => navigate('/blog')} className="back-button">
              ← Back to Blog
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <Header />
      <main className="post-container">
        <article className="post">
          <h1 className="post-title">{post.title}</h1>
          <div className="post-meta">
            <div className="post-meta-details">
              <span>{formatDate(post.created_at)}</span>
              <span>by The Learning Dictionary</span>
            </div>
          </div>
          {post.featured_image && (
            <img 
              src={post.featured_image} 
              alt={post.title} 
              className="post-featured-image"
            />
          )}
          <div 
            className="post-body"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>

        {/* Add subscription component for specific blog post */}
        {slug === 'why-i-built-this-dictionary' && (
          <div className="post-subscription">
            <Subscription />
          </div>
        )}

        <button onClick={() => navigate('/blog')} className="back-button">
          ← Back to Blog
        </button>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage; 